import { MISSING_NUMBER_PLACEHOLDER } from "../components/DialControls";
import {
  PabxContactItem,
  PabxFavorite,
  TeamsContactItem,
  TeamsFavorite,
} from "../types/contacts";

/**
 * Conversão entre o item de contato exibido e o favorito gravado no backend.
 *
 * As duas direções existem porque os formatos são diferentes de propósito: cada origem guarda os
 * campos que realmente tem (nome + quatro números no PABX; displayName + telefones no Teams), em
 * vez de um formato comum com metade dos campos vazios. O preço é este arquivo.
 *
 * Os números ausentes são `"---"` na exibição (`MISSING_NUMBER_PLACEHOLDER`) e `""` no
 * armazenamento — gravar o placeholder faria o backend guardar um "número" que não é discável.
 */

function stored(number: string) {
  return number === MISSING_NUMBER_PLACEHOLDER ? "" : number;
}

function displayed(number: string) {
  return number || MISSING_NUMBER_PLACEHOLDER;
}

export function pabxContactToFavorite(item: PabxContactItem): PabxFavorite | null {
  if (!item.id) return null;
  return {
    id: item.id,
    name: item.name,
    numbers: {
      business: stored(item.businessNumber),
      mobile: stored(item.mobileNumber),
      extension: stored(item.extensionNumber),
      home: stored(item.homeNumber),
    },
  };
}

export function teamsContactToFavorite(item: TeamsContactItem): TeamsFavorite | null {
  if (!item.id) return null;
  return {
    id: item.id,
    displayName: item.displayName,
    mobilePhone: stored(item.mobilePhone),
    businessPhones: item.businessPhones.map(stored).filter(Boolean),
  };
}

export function pabxFavoriteToContact(favorite: PabxFavorite): PabxContactItem {
  return {
    id: favorite.id,
    name: favorite.name,
    businessNumber: displayed(favorite.numbers?.business || ""),
    mobileNumber: displayed(favorite.numbers?.mobile || ""),
    extensionNumber: displayed(favorite.numbers?.extension || ""),
    homeNumber: displayed(favorite.numbers?.home || ""),
  };
}

export function teamsFavoriteToContact(favorite: TeamsFavorite): TeamsContactItem {
  const businessPhones = favorite.businessPhones || [];
  return {
    id: favorite.id,
    displayName: favorite.displayName,
    mobilePhone: displayed(favorite.mobilePhone),
    businessPhones: [0, 1, 2].map((position) => displayed(businessPhones[position] || "")),
  };
}

/**
 * Compara a cópia gravada com o contato que acabou de chegar da agenda. É o que decide se a
 * reconciliação precisa gravar: sem esta comparação, abrir a aba mandaria um PATCH a cada vez.
 */
export function teamsFavoriteDiffers(favorite: TeamsFavorite, contact: TeamsContactItem) {
  const fresh = teamsContactToFavorite(contact);
  if (!fresh) return false;
  return (
    favorite.displayName !== fresh.displayName ||
    favorite.mobilePhone !== fresh.mobilePhone ||
    (favorite.businessPhones || []).join("\u0000") !== fresh.businessPhones.join("\u0000")
  );
}

export function pabxFavoriteDiffers(favorite: PabxFavorite, contact: PabxContactItem) {
  const fresh = pabxContactToFavorite(contact);
  if (!fresh) return false;
  return (
    favorite.name !== fresh.name ||
    (favorite.numbers?.business || "") !== fresh.numbers.business ||
    (favorite.numbers?.mobile || "") !== fresh.numbers.mobile ||
    (favorite.numbers?.extension || "") !== fresh.numbers.extension ||
    (favorite.numbers?.home || "") !== fresh.numbers.home
  );
}
